import React from 'react'
import { Check } from 'lucide-react'

export interface CheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** Red outline when unchecked — for a required/invalid gate. */
  error?: boolean
  id?: string
  'aria-label'?: string
  'aria-controls'?: string
  'aria-expanded'?: boolean
  /** Extra styles merged onto the box (e.g. marginTop to top-align with wrapping label text). */
  style?: React.CSSProperties
}

/**
 * The one and only checkbox design.
 *
 * An 18px rounded square (`radius-xs`) that sits on `surface-container-lowest`
 * with the canonical `--border-field-width` `field-border` border when
 * unchecked, and fills with the
 * `--gradient-brand` blue + a white lucide `Check` when checked — visually
 * equal to a completed progress-stepper node. Used for every checkbox in the
 * app (license "no-licence", director authorised-signatory, review
 * confirmation). Do not hand-roll a checkbox or fall back to a native
 * `accentColor` input — use this component so the design stays consistent.
 */
export function Checkbox({ checked, onChange, error = false, style, ...rest }: CheckboxProps) {
  return (
    <span
      style={{
        position: 'relative',
        width: 18,
        height: 18,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-xs)',
        background: checked
          ? 'var(--gradient-brand)'
          : 'var(--color-surface-container-lowest)',
        border: checked
          ? 'none'
          : error
            ? 'var(--border-field-width) solid var(--color-error-border)'
            : 'var(--border-field-width) solid var(--color-field-border)',
        transition: 'background 0.15s ease, border-color 0.15s ease',
        ...style,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          position: 'absolute',
          inset: 0,
          margin: 0,
          opacity: 0,
          cursor: 'pointer',
        }}
        {...rest}
      />
      {checked && (
        <Check
          size={13}
          color="var(--color-text-on-primary)"
          strokeWidth={3}
          aria-hidden="true"
          style={{ pointerEvents: 'none' }}
        />
      )}
    </span>
  )
}
